import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { setAuthHeaderSource, setUnauthorizedHandler } from '@/api/client'
import { readStorage, writeStorage } from '@/lib/storage'
import { AuthContext, DEV_ROLES, type AuthContextValue, type DevRole } from './context'

const ROLE_KEY = 'devRole'
const DEV_USER = 'dev@mailsmith.local'

function readRole(): DevRole {
  const r = readStorage(ROLE_KEY)
  return DEV_ROLES.includes(r as DevRole) ? (r as DevRole) : 'admin'
}

// Install the header source at module load so the first request is already
// authenticated; the provider below keeps it in sync with role changes.
let currentRole: DevRole = readRole()
const devHeaders = async () => ({ 'X-Dev-Role': currentRole, 'X-Dev-User': DEV_USER })

/**
 * Local development auth. No sign-in: the user picks a role from the user
 * menu, which is sent to the API as X-Dev-Role / X-Dev-User. The API only
 * honors these headers when it runs with AUTH_MODE=dev.
 */
export function DevAuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [role, setRole] = useState<DevRole>(currentRole)

  // Synchronous install (idempotent) so children's first queries carry headers.
  setAuthHeaderSource(devHeaders)
  setUnauthorizedHandler(undefined)

  const setDevRole = useCallback(
    (next: DevRole) => {
      currentRole = next
      writeStorage(ROLE_KEY, next)
      setRole(next)
      // Everything the API returned may depend on the role.
      void queryClient.resetQueries()
    },
    [queryClient],
  )

  const value = useMemo<AuthContextValue>(
    () => ({
      mode: 'dev',
      account: { name: `Dev ${role}`, username: DEV_USER },
      signOut: () => setDevRole('viewer'),
      devRole: role,
      setDevRole,
    }),
    [role, setDevRole],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
